import config from '@frolight/config';
import HTTPStatusError from '@frolight/utils/HTTPStatusError';

const ROOT_URL = `${config.api.uri}/users`;

const CREATE_URL = `${ROOT_URL}/`
const PATCH_URL = `${ROOT_URL}/:userId`;
const VERIFICATION_TOKEN_URL = `${ROOT_URL}/verification-token/:verificationToken`;
const VERIFY_URL = `${ROOT_URL}/verify`;

export const createUser = async (abortController, { firstName, lastName, email, mobilePhoneNrCountry, mobilePhoneNrNationalNumber, password, currentLocale }) => {
  try {
    const response = await fetch(CREATE_URL, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json; charset=UTF-8'
      },
      signal: abortController.signal,
      body: JSON.stringify({
        firstName, lastName, email, password, mobilePhoneNrCountry, mobilePhoneNrNationalNumber, currentLocale
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new HTTPStatusError(response.status, response.statusText, errorData?.error);
    }

    const data = await response.json();
    return data;
  } catch (err) {
    if (err.name !== 'AbortError') {
      if (err instanceof HTTPStatusError) {
        throw err;
      } else {
        throw new Error(`Error signing up: ${err.message}`);
      }
    }
  }
};

export const getUnverifiedUserByVerificationToken = async (abortController, { verificationToken }) => {
  try {
    const response = await fetch(VERIFICATION_TOKEN_URL.replace(':verificationToken', verificationToken), {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      },
      signal: abortController.signal,
    });

    if (!response.ok) {
      throw new HTTPStatusError(response.status, response.statusText);
    }
    const data = await response.json();
    return data;
  } catch (err) {
    if (err.name !== 'AbortError') {
      if (err instanceof HTTPStatusError) {
        throw err;
      } else {
        throw new Error(`Error getting unverified user with verification token "${verificationToken}": ${err.message}`);
      }
    }
  }
};

export const verifyUser = async (abortController, { email, password, verificationToken }) => {
  return fetch(VERIFY_URL, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json; charset=UTF-8'
    },
    signal: abortController.signal,
    body: JSON.stringify({ email, password, verificationToken })
  })
    .then(response => {
      if (response.ok) {
        return response.json();
      }
      throw new HTTPStatusError(response.status, response.statusText);
    })
    .then(data => data)
    .catch(err => {
      if (err.name !== 'AbortError') {
        if (err instanceof HTTPStatusError) {
          throw err;
        } else {
          throw new Error(`Error verifying user: ${err.message}`);
        }
      }
    });
};

export const assignUserToOrganisation = async (abortController, accessToken, { userId, organisationId }) => {
  try {
    const response = await fetch(PATCH_URL.replace(':userId', userId), {
      method: 'PATCH',
      headers: {
        'Accept': 'application/json',
        'Authorization': `${accessToken.type} ${accessToken.token}`,
        'Content-Type': 'application/json; charset=UTF-8'
      },
      body: JSON.stringify({
        organisationId: organisationId
      }),
      signal: abortController.signal
    });

    if (!response.ok) {
      throw new HTTPStatusError(response.status, response.statusText);
    }

    const data = await response.json();
    return data;
  } catch (err) {
    if (err.name !== 'AbortError') {
      if (err instanceof HTTPStatusError) {
        throw err;
      } else {
        throw new Error(`Error assigning user to organisation: ${err.message}`);
      }
    }
  }
};
